import {useQuery} from "@tanstack/react-query";

import {getPagination} from "@/src/modules/core/lib";
import {ITEMS_PER_PAGE} from "@/src/modules/core/utils/constants";

import {safelistingsService} from "../services";

type UseSafelistingsPaginatedParams = {
  rows?: number;
  page: number;
  filter?: Record<string, unknown>;
  sort?: string;
  enabled?: boolean;
};

export function useSafelistingsPaginated({
  rows = ITEMS_PER_PAGE,
  page,
  filter = {},
  sort = "-created_at",
  enabled = true,
}: UseSafelistingsPaginatedParams) {
  const filterObj = Object.fromEntries(
    Object.entries(filter).filter(
      ([, v]) =>
        v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0),
    ),
  ) as Record<string, string | number>;

  const options = {
    page: {limit: rows, offset: (page - 1) * rows},
    filter: filterObj,
    sort,
  };

  const {data, isSuccess, isPending, isError, error, isFetching} = useQuery({
    queryKey: ["safelistings", options],
    queryFn: () => safelistingsService.getSafelistings(options),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled,
  });

  const safelistings = isSuccess && data ? data.data : [];
  const totalRecords = data?.meta?.total ?? 0;
  const {start, end} = getPagination(totalRecords, rows, page);

  return {
    safelistings,
    totalRecords,
    start,
    end,
    isSuccess,
    isPending,
    isError,
    error,
    isFetching,
  };
}
